<template>
  <nav class="bg-white border-gray-200 dark:bg-custom-green-50" role="navigation">
    <div class="max-w-screen-xl flex flex-wrap items-center justify-between mx-auto p-4">
      <!-- Logo -->
      <RouterLink to="/" class="flex items-center space-x-3 rtl:space-x-reverse">
        <span class="self-center text-2xl font-semibold whitespace-nowrap dark:text-white text-black">
          PRODESSA
        </span>
      </RouterLink>

      <!-- Mobile Menu Button -->
      <button
        @click="toggleMenu"
        type="button"
        class="inline-flex items-center p-2 w-10 h-10 justify-center text-sm text-gray-500 rounded-lg md:hidden hover:bg-green focus:outline-none focus:ring-2 focus:ring-dark-green dark:text-gray-400 dark:hover:bg-custom-green-50 dark:focus:ring-cyan"
        aria-controls="navbar-default"
        :aria-expanded="isMenuOpen.toString()"
        aria-label="Toggle Menu"
      >
        <span class="sr-only">Open main menu</span>
        <svg class="w-5 h-5" fill="none" viewBox="0 0 17 14">
          <path
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M1 1h15M1 7h15M1 13h15"
          />
        </svg>
      </button>

      <!-- Navbar Links -->
      <div :class="{ block: isMenuOpen, hidden: !isMenuOpen }" class="w-full md:block md:w-auto" id="navbar-default">
        <ul class="font-medium flex flex-col p-4 md:p-0 mt-4 border border-custom-green-50 rounded-lg bg-white md:flex-row md:space-x-8 rtl:space-x-reverse md:mt-0 md:border-0 md:bg-light-green dark:bg-deep-green md:dark:bg-custom-green dark:border-gray-700 md:items-center">
          <!-- Inicio -->
          <li class="flex items-center">
            <RouterLink
              to="/"
              class="nav-link block py-2 px-3 rounded md:bg-transparent md:p-0 dark:text-white"
              exact-active-class="active"
            >
              Inicio
            </RouterLink>
          </li>

          <!-- Nuestra Empresa Dropdown -->
          <li class="relative flex items-center" @mouseenter="showEmpresa = true" @mouseleave="showEmpresa = false">
            <span class="nav-link block py-2 px-3 cursor-pointer dark:text-white">Nuestra Empresa</span>
            <ul
              v-show="showEmpresa"
              class="absolute left-0 top-full bg-white dark:bg-custom-green-100 text-gray-900 dark:text-white shadow-lg rounded-md z-10 w-64 border border-gray-200 dark:border-gray-700"
            >
              <li>
                <RouterLink
                  to="/about"
                  class="block px-4 py-2 hover:bg-gray-200 dark:hover:bg-custom-green-200"
                >Sobre Nosotros</RouterLink>
              </li>
              <li>
                <RouterLink
                  to="/organization"
                  class="block px-4 py-2 hover:bg-gray-200 dark:hover:bg-custom-green-200"
                >Organización</RouterLink>
              </li>
              <li>
                <RouterLink
                  to="/financial"
                  class="block px-4 py-2 hover:bg-gray-200 dark:hover:bg-custom-green-200"
                >Estados Financieros</RouterLink>
              </li>
            </ul>
          </li>

          <!-- Productos Dropdown -->
          <li class="relative flex items-center" @mouseenter="showProductos = true" @mouseleave="showProductos = false">
            <span class="nav-link block py-2 px-3 cursor-pointer dark:text-white">Productos</span>
            <ul
              v-show="showProductos"
              class="absolute left-0 top-full bg-white dark:bg-custom-green-100 text-gray-900 dark:text-white shadow-lg rounded-md z-10 w-64 border border-gray-200 dark:border-gray-700"
            >
              <li>
                <RouterLink
                  to="/credits"
                  class="block px-4 py-2 hover:bg-gray-200 dark:hover:bg-custom-green-200"
                >Créditos Individuales y MicroCréditos</RouterLink>
              </li>
              <li>
                <RouterLink
                  to="/productrates"
                  class="block px-4 py-2 hover:bg-gray-200 dark:hover:bg-custom-green-200"
                >Tarifario de Productos</RouterLink>
              </li>
            </ul>
          </li>

          <!-- Gestión de Clientes Dropdown -->
          <li class="relative flex items-center" @mouseenter="showClientes = true" @mouseleave="showClientes = false">
            <span class="nav-link block py-2 px-3 cursor-pointer dark:text-white">Gestión de Clientes</span>
            <ul
              v-show="showClientes"
              class="absolute left-0 top-full bg-white dark:bg-custom-green-100 text-gray-900 dark:text-white shadow-lg rounded-md z-10 w-64 border border-gray-200 dark:border-gray-700"
            >
              <li>
                <RouterLink
                  to="/complaints"
                  class="block px-4 py-2 hover:bg-gray-200 dark:hover:bg-custom-green-200"
                >Consultas o Reclamos</RouterLink>
              </li>
              <li>
                <RouterLink
                  to="/education"
                  class="block px-4 py-2 hover:bg-gray-200 dark:hover:bg-custom-green-200"
                >Educación Financiera</RouterLink>
              </li>
            </ul>
          </li>

          <!-- Contáctanos -->
          <li class="flex items-center">
            <RouterLink
              to="/contact"
              class="nav-link block py-2 px-3 rounded md:bg-transparent md:p-0 dark:text-white"
              exact-active-class="active"
            >
              Contáctanos
            </RouterLink>
          </li>

          <!-- Dark Mode Toggle -->
          <li class="flex items-center">
            <button
              @click="toggleDarkMode"
              aria-label="Toggle Dark Mode"
              class="ml-4 p-2 rounded-lg border dark:border-gray-600 focus:outline-none"
            >
              <span class="dark:hidden">
                <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24">
                  <path
                    d="M3.32 11.68c0 4.97 4.03 9 9 9 3.79 0 7.03-2.34 8.36-5.65A8.97 8.97 0 0 1 17.32 15.68c-4.97 0-9-4.03-9-9 0-1.18.23-2.31.64-3.35A8.98 8.98 0 0 0 3.32 11.68Z"
                    stroke="#000"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
              <span class="hidden dark:inline">
                <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24">
                  <path
                    d="M12 3v1M12 20v1M4 12H3M6.31 6.31 5.5 5.5M17.69 6.31 18.5 5.5M6.31 17.69 5.5 18.5M17.69 17.69 18.5 18.5M21 12h-1M16 12a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z"
                    stroke="#fff"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
            </button>
          </li>
        </ul>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { ref } from "vue";
const isMenuOpen = ref(false);

const showEmpresa = ref(false);
const showProductos = ref(false);
const showClientes = ref(false);

function toggleMenu() {
  isMenuOpen.value = !isMenuOpen.value;
}

function toggleDarkMode() {
  if (document.documentElement.classList.contains("dark")) {
    document.documentElement.classList.remove("dark");
    localStorage.setItem("theme", "light");
  } else {
    document.documentElement.classList.add("dark");
    localStorage.setItem("theme", "dark");
  }
}

if (
  localStorage.getItem("theme") === "dark" ||
  (!localStorage.getItem("theme") &&
    window.matchMedia("(prefers-color-scheme: dark)").matches)
) {
  document.documentElement.classList.add("dark");
} else {
  document.documentElement.classList.remove("dark");
}
</script>

<style scoped>
.router-link-active,
.router-link-exact-active {
  @apply text-white bg-custom-green md:bg-transparent md:text-custom-green-900 dark:text-white md:dark:text-gray-500;
}

a:hover {
  @apply text-white md:hover:text-custom-green-900 dark:hover:text-gray-800;
}
</style>
